import {
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import { useState, useRef } from "react";
import AssignmentIcon from "@mui/icons-material/Assignment";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useTheme } from "@mui/material/styles";
import {
  createUploadLegalFile,
  previewUploadLawyers,
} from "../../services/LegalFileService";
import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import Tooltip from "@mui/material/Tooltip";
import GlassCard from "../General/GlassCard";
import CardHeader from "../General/CardHeader";
import FileUploadBox from "../General/FileUploadBox";
import OnlineQueryTooltip, {
  useOnlineQueryGate,
} from "../General/OnlineQueryGate";
import { useToast } from "../../context/ToastContext";
import LawyerSessionRequiredDialog from "../General/Dialog/LawyerSessionRequiredDialog";
import useLawyerSessionQueue from "../../hooks/useLawyerSessionQueue";

export default function LegalFileCharge() {
  const { showToast } = useToast();
  const { online } = useOnlineQueryGate();
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const sessionQueue = useLawyerSessionQueue();

  const [loading, setLoading] = useState(false);

  const [action, setAction] = useState("import_legal_files");
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);

  const [openHelp, setOpenHelp] = useState(false);
  const handleOpenHelp = () => setOpenHelp(true);
  const handleCloseHelp = () => setOpenHelp(false);

  const handleFileSelect = (file) => {
    if (file) {
      const validExtensions = ["xls", "xlsx"];
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (!validExtensions.includes(fileExtension)) {
        setErrors({
          ...errors,
          numerosExpedientes: "Solo se permiten archivos .xls o .xlsx",
        });
        setSelectedFile(null);
        return;
      }
      setErrors({ ...errors, numerosExpedientes: "" });
      setSelectedFile(file);
    }
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("numerosExpedientes", selectedFile);
    formData.append("action", action);
    return formData;
  };

  const submitFile = async () => {
    setLoading(true);
    try {
      const response = await createUploadLegalFile(buildFormData());

      showToast(response.data.message, "success");
      setSelectedFile(null);
      setErrors({});

      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        showToast(error.response.data?.message || "Ocurrió un error.", "error");
      } else {
        console.log(error);
        showToast("Ocurrió un error inesperado.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!selectedFile) {
      showToast("Debe seleccionar un archivo.", "info");
      return;
    }

    try {
      await sessionQueue.checkAndRun(
        () => previewUploadLawyers(buildFormData()),
        submitFile
      );
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        showToast(error.response.data?.message || "Ocurrió un error.", "error");
      } else {
        console.log(error);
        showToast("Ocurrió un error inesperado.", "error");
      }
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del Excel" arrow>
              <HelpOutlineIcon
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": { color: theme.palette.primary.dark },
                }}
                onClick={handleOpenHelp}
              />
            </Tooltip>
          </Box>

          <CardHeader
            icon={<AssignmentIcon />}
            title="Carga de Expedientes"
            description="Cargar expedientes o ubicaciones de expedientes de SAC desde un archivo Excel"
          />

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <TextField
                  select
                  label="Acción"
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="import_legal_files">
                    MIGRAR EXPEDIENTES ( ALTA / ACTUALIZACION COMPLETA)
                  </MenuItem>
                  <MenuItem value="update_locations">
                    ACTUALIZAR UBICACIONES (SOLO CAMPO UBICACION)
                  </MenuItem>
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <FileUploadBox
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  accept=".xls,.xlsx"
                  placeholder="Seleccionar Excel"
                  helperText="O arrastrá el Excel aquí (.xls, .xlsx)"
                />
                {errors.numerosExpedientes && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {errors.numerosExpedientes}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <OnlineQueryTooltip show={!online} block>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={loading || !online || sessionQueue.isPending}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : action === "update_locations" ? (
                    "Actualizar Ubicaciones"
                  ) : (
                    "Cargar Expedientes"
                  )}
                </Button>
                </OnlineQueryTooltip>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={handleCloseHelp}
        title="Requisitos del archivo Excel"
        description="El archivo debe ser formato .xls o .xlsx."
        columns={
          action === "import_legal_files"
            ? ["Código de Expediente", "Nombre del Abogado"]
            : ["Código de Expediente"]
        }
        example={
          action === "import_legal_files"
            ? ["12345678", "MATIAS A MATE PALOU"]
            : ["12345678"]
        }
      />

      <LawyerSessionRequiredDialog {...sessionQueue.dialogProps} />
    </Grid>
  );
}
